import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { AppShell, PageHeader } from "@/components/AppShell";
import {
	Loader2,
	Play,
	Square,
	Home,
	ArrowLeft,
	Volume2,
	VolumeX,
	Keyboard,
	RefreshCw,
	Heart,
	ArrowUp,
	UserPlus,
	Search,
	MessageSquare,
	Bot,
	Hand,
	Smartphone,
	PanelLeftOpen,
	Send,
	AlertCircle,
	CheckCircle2,
	Upload,
} from "lucide-react";
import { toast } from 'sonner';
import { API_BASE_URL, getAuthToken } from '@/services/api';
import {
	fetchDevices,
	setSyncGroup,
	setMasterDevice,
	sendTouch,
	sendKey,
	executeTikTokAction,
	startAgent,
	stopAgent,
	getAgentStreamUrl,
	uploadFileToDevices,
	type VMOSDevice,
	type DevicesResponse,
} from '@/services/vmos-api';
import { cn, toAscii } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';

// === LiveScreenshot Component ===

const LiveScreenshot = ({
	padId,
	interval = 2000,
	quality = 70,
	disabled = false,
	className = "",
	onClick,
}: {
	padId: string;
	interval?: number;
	quality?: number;
	disabled?: boolean;
	className?: string;
	onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}) => {
	const prevUrlRef = useRef<string | null>(null);

	const { data: imageSrc, isLoading, isError } = useQuery({
		queryKey: ['vmos-screenshot', padId, quality],
		queryFn: async () => {
			const token = await getAuthToken();
			if (!token) throw new Error('Not authenticated');

			const response = await fetch(
				`${API_BASE_URL}/vmos-group-control/devices/${padId}/screenshot?quality=${quality}&t=${Date.now()}`,
				{ headers: { Authorization: `Bearer ${token}` } }
			);

			if (!response.ok) throw new Error('Failed to fetch screenshot');

			const blob = await response.blob();
			const url = URL.createObjectURL(blob);

			if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
			prevUrlRef.current = url;
			return url;
		},
		refetchInterval: disabled ? false : interval,
		enabled: !disabled,
		retry: 2,
		staleTime: 0,
	});

	// Note: no revoke-on-unmount — the blob URL is React Query cache data, so
	// revoking it on unmount would leave a cached `<img>` pointing at a freed
	// object. The previous URL is revoked when this instance replaces it.
	if (isLoading && !imageSrc) {
		return (
			<div className={cn("flex items-center justify-center bg-muted rounded-lg", className)}>
				<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
			</div>
		);
	}

	if (isError && !imageSrc) {
		return (
			<div className={cn("flex items-center justify-center bg-muted rounded-lg", className)}>
				<span className="text-xs text-muted-foreground">No screenshot</span>
			</div>
		);
	}

	return (
		<div className={cn("relative overflow-hidden rounded-lg bg-black", className)} onClick={onClick}>
			{imageSrc && (
				<img
					src={imageSrc}
					alt="Device screenshot"
					className="w-full h-full object-contain"
					draggable={false}
				/>
			)}
		</div>
	);
};

// === Setup Mode ===

const SetupMode = ({
	devices,
	selectedIds,
	onToggle,
	onStart,
	isLoading,
}: {
	devices: VMOSDevice[];
	selectedIds: Set<string>;
	onToggle: (padId: string) => void;
	onStart: () => void;
	isLoading: boolean;
}) => {
	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-lg font-semibold">Select Devices</h2>
					<p className="text-sm text-muted-foreground">
						Choose which devices to include in group control
					</p>
				</div>
				<Button
					onClick={onStart}
					disabled={selectedIds.size < 1 || isLoading}
				>
					{isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
					Start Group Control ({selectedIds.size})
				</Button>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
				{devices.map((device) => (
					<Card
						key={device.pad_id}
						className={cn(
							"cursor-pointer transition-colors",
							selectedIds.has(device.pad_id) && "ring-2 ring-primary"
						)}
						onClick={() => onToggle(device.pad_id)}
					>
						<CardContent className="p-4">
							<div className="flex items-start gap-3">
								<Checkbox
									checked={selectedIds.has(device.pad_id)}
									onCheckedChange={() => onToggle(device.pad_id)}
								/>
								<div className="flex-1 min-w-0">
									<div className="flex items-center gap-2">
										<Smartphone className="h-4 w-4 text-muted-foreground" />
										<span className="font-medium truncate">{device.device_name}</span>
									</div>
									<div className="text-xs text-muted-foreground mt-1 space-y-0.5">
										<div>Pad ID: {device.pad_id}</div>
										<div className="flex items-center gap-2">
											<Badge variant="outline" className="text-xs">{device.platform}</Badge>
											{device.username && (
												<span>@{device.username}</span>
											)}
										</div>
										<div>{device.screen_width}x{device.screen_height}</div>
									</div>
								</div>
								<div className={cn(
									"w-2 h-2 rounded-full",
									device.status === "active" ? "bg-green-500" : "bg-gray-400"
								)} />
							</div>
						</CardContent>
					</Card>
				))}
			</div>

			{devices.length === 0 && (
				<div className="text-center py-12 text-muted-foreground">
					<Smartphone className="h-12 w-12 mx-auto mb-4 opacity-50" />
					<p>No devices found.</p>
					<p className="text-sm mt-2">Run the import script to add devices.</p>
				</div>
			)}
		</div>
	);
};

// === Control Button Bar ===

const ControlButtonBar = ({ onKey }: { onKey: (keycode: number) => void }) => {
	return (
		<div className="flex flex-col gap-2">
			<Button variant="outline" size="icon" onClick={() => onKey(3)} title="Home">
				<Home className="h-4 w-4" />
			</Button>
			<Button variant="outline" size="icon" onClick={() => onKey(4)} title="Back">
				<ArrowLeft className="h-4 w-4" />
			</Button>
			<Button variant="outline" size="icon" onClick={() => onKey(24)} title="Volume Up">
				<Volume2 className="h-4 w-4" />
			</Button>
			<Button variant="outline" size="icon" onClick={() => onKey(25)} title="Volume Down">
				<VolumeX className="h-4 w-4" />
			</Button>
			<Button variant="outline" size="icon" onClick={() => onKey(187)} title="Recent Apps">
				<Keyboard className="h-4 w-4" />
			</Button>
		</div>
	);
};

// === Follower Device Grid ===

const FollowerDeviceGrid = ({
	devices,
	syncedIds,
	masterPadId,
	onToggleSync,
	onPromoteToMaster,
}: {
	devices: VMOSDevice[];
	syncedIds: Set<string>;
	masterPadId: string;
	onToggleSync: (padId: string) => void;
	onPromoteToMaster: (padId: string) => void;
}) => {
	const followers = devices.filter(d => d.pad_id !== masterPadId);

	return (
		<div className="grid grid-cols-2 gap-2 overflow-y-auto max-h-[calc(100vh-300px)]">
			{followers.map((device) => (
				<Card
					key={device.pad_id}
					className="p-2 cursor-pointer hover:bg-accent/50 transition-colors"
					onClick={() => onPromoteToMaster(device.pad_id)}
				>
					<div className="flex items-center gap-2 mb-1">
						<Checkbox
							checked={syncedIds.has(device.pad_id)}
							onCheckedChange={(e) => {
								e; // prevent card click
								onToggleSync(device.pad_id);
							}}
							onClick={(e) => e.stopPropagation()}
						/>
						<span className="text-xs font-medium truncate flex-1">{device.device_name}</span>
						<div className={cn(
							"w-1.5 h-1.5 rounded-full",
							syncedIds.has(device.pad_id) ? "bg-green-500" : "bg-gray-400"
						)} />
					</div>
					<LiveScreenshot
						padId={device.pad_id}
						interval={2000}
						quality={40}
						className="h-32 w-full"
					/>
				</Card>
			))}
		</div>
	);
};

// === File Upload Panel ===

const FileUploadPanel = ({
	syncedPadIds,
}: {
	syncedPadIds: string[];
}) => {
	const [file, setFile] = useState<File | null>(null);
	const [destinationPath, setDestinationPath] = useState('/DCIM/');
	const [autoInstall, setAutoInstall] = useState(false);
	const [uploading, setUploading] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const isApk = file?.name?.toLowerCase().endsWith('.apk') ?? false;

	const handleUpload = async () => {
		if (!file || syncedPadIds.length === 0) return;

		setUploading(true);
		try {
			const result = await uploadFileToDevices({
				file,
				padIds: syncedPadIds,
				destinationPath,
				autoInstall: isApk ? autoInstall : false,
			});
			toast.success(`File uploaded to ${result.pad_ids.length} device(s)`);
			setFile(null);
			if (fileInputRef.current) fileInputRef.current.value = '';
		} catch (err: any) {
			toast.error(err.message || 'Upload failed');
		} finally {
			setUploading(false);
		}
	};

	return (
		<div className="flex items-center gap-2">
			<input
				ref={fileInputRef}
				type="file"
				className="hidden"
				onChange={(e) => setFile(e.target.files?.[0] || null)}
			/>
			<Button
				variant="outline"
				size="sm"
				onClick={() => fileInputRef.current?.click()}
			>
				<Upload className="h-3 w-3 mr-1" />
				{file ? file.name.slice(0, 20) : 'Choose File'}
			</Button>
			<Input
				placeholder="Dest path"
				value={destinationPath}
				onChange={(e) => setDestinationPath(e.target.value)}
				className="w-24 h-8 text-xs"
			/>
			{isApk && (
				<label className="flex items-center gap-1 text-xs">
					<Checkbox
						checked={autoInstall}
						onCheckedChange={(v) => setAutoInstall(!!v)}
					/>
					Auto-install
				</label>
			)}
			<Button
				size="sm"
				disabled={!file || syncedPadIds.length === 0 || uploading}
				onClick={handleUpload}
			>
				{uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
				Upload ({syncedPadIds.length})
			</Button>
		</div>
	);
};

// === Bottom Control Bar (Manual mode only) ===

const BottomControlBar = ({
	onTikTokAction,
	syncedPadIds,
}: {
	onTikTokAction: (action: string, text?: string) => void;
	syncedPadIds: string[];
}) => {
	const [searchQuery, setSearchQuery] = useState('');
	const [showUpload, setShowUpload] = useState(false);

	return (
		<div className="border-t bg-background p-3 space-y-2">
			<div className="flex items-center gap-2 flex-wrap">
				<Button variant="outline" size="sm" onClick={() => onTikTokAction('scroll_fyp')}>
					<ArrowUp className="h-3 w-3 mr-1" /> Scroll
				</Button>
				<Button variant="outline" size="sm" onClick={() => onTikTokAction('like_video')}>
					<Heart className="h-3 w-3 mr-1" /> Like
				</Button>
				<Button variant="outline" size="sm" onClick={() => onTikTokAction('follow_creator')}>
					<UserPlus className="h-3 w-3 mr-1" /> Follow
				</Button>
				<Button variant="outline" size="sm" onClick={() => onTikTokAction('open_comments')}>
					<MessageSquare className="h-3 w-3 mr-1" /> Comments
				</Button>
				<div className="flex gap-1">
					<Input
						placeholder="Search..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="w-32 h-8 text-xs"
						onKeyDown={(e) => {
							if (e.key === 'Enter' && searchQuery) {
								onTikTokAction('search', searchQuery);
								setSearchQuery('');
							}
						}}
					/>
					<Button
						variant="outline"
						size="sm"
						onClick={() => {
							if (searchQuery) {
								onTikTokAction('search', searchQuery);
								setSearchQuery('');
							}
						}}
					>
						<Search className="h-3 w-3" />
					</Button>
				</div>
				<Button
					variant={showUpload ? "default" : "outline"}
					size="sm"
					onClick={() => setShowUpload(!showUpload)}
				>
					<Upload className="h-3 w-3 mr-1" /> Upload File
				</Button>
			</div>
			{showUpload && (
				<FileUploadPanel syncedPadIds={syncedPadIds} />
			)}
		</div>
	);
};

// === Agent Chat Panel ===

const AgentChatPanel = ({
	agentTask,
	onAgentTaskChange,
	onAgentStart,
	onAgentStop,
	agentRunning,
	agentEvents,
	maxSteps,
	onMaxStepsChange,
}: {
	agentTask: string;
	onAgentTaskChange: (task: string) => void;
	onAgentStart: () => void;
	onAgentStop: () => void;
	agentRunning: boolean;
	agentEvents: any[];
	maxSteps: number;
	onMaxStepsChange: (steps: number) => void;
}) => {
	const messagesEndRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [agentEvents]);

	// Build chat messages from events
	const renderEvent = (event: any, index: number) => {
		const { event: type, data } = event;

		if (type === 'thought') {
			return (
				<div key={index} className="flex justify-start">
					<div className="max-w-[85%] rounded-lg px-3 py-2 bg-muted text-sm">
						<div className="text-[10px] font-medium text-muted-foreground mb-0.5">Thinking</div>
						<p className="text-foreground">{data.thought}</p>
					</div>
				</div>
			);
		}

		if (type === 'action') {
			return (
				<div key={index} className="flex justify-start">
					<div className="max-w-[85%] rounded-lg px-3 py-2 bg-muted text-sm">
						<div className="flex items-center gap-1.5 mb-0.5">
							<Badge variant="secondary" className="text-[10px]">{data.action_type}</Badge>
							<span className="text-[10px] text-muted-foreground">
								{data.status}
							</span>
						</div>
						{data.target && data.target !== '{}' && (
							<p className="text-xs text-muted-foreground font-mono">{data.target}</p>
						)}
					</div>
				</div>
			);
		}

		if (type === 'progress') {
			return (
				<div key={index} className="flex justify-center">
					<span className="text-[10px] text-muted-foreground px-2 py-0.5">
						{data.message}
					</span>
				</div>
			);
		}

		if (type === 'error') {
			return (
				<div key={index} className="flex justify-start">
					<div className="max-w-[85%] rounded-lg px-3 py-2 bg-destructive/10 text-sm">
						<div className="flex items-center gap-1.5 mb-0.5">
							<AlertCircle className="h-3 w-3 text-destructive" />
							<span className="text-[10px] font-medium text-destructive">Error</span>
						</div>
						<p className="text-destructive">{data.error}</p>
					</div>
				</div>
			);
		}

		if (type === 'result') {
			return (
				<div key={index} className="flex justify-start">
					<div className="max-w-[85%] rounded-lg px-3 py-2 bg-green-500/10 text-sm">
						<div className="flex items-center gap-1.5 mb-0.5">
							<CheckCircle2 className="h-3 w-3 text-green-600" />
							<span className="text-[10px] font-medium text-green-600">
								{data.status === 'completed' ? 'Completed' : data.status === 'stopped' ? 'Stopped' : 'Done'}
							</span>
							{data.total_steps && (
								<span className="text-[10px] text-muted-foreground">
									({data.total_steps} steps, {Math.round(data.duration_seconds || 0)}s)
								</span>
							)}
						</div>
						<p className="text-green-400">{data.result}</p>
					</div>
				</div>
			);
		}

		// Fallback for unknown event types
		return (
			<div key={index} className="flex justify-center">
				<span className="text-[10px] text-muted-foreground px-2 py-0.5">
					{type}: {JSON.stringify(data).slice(0, 80)}
				</span>
			</div>
		);
	};

	return (
		<div className="flex flex-col h-full border-t">
			{/* Header */}
			<div className="px-3 py-2 border-b">
				<div className="text-xs font-semibold text-muted-foreground">AI AGENT CHAT</div>
			</div>

			{/* Chat messages area */}
			<ScrollArea className="flex-1 min-h-0">
				<div className="p-3 space-y-2">
					{agentEvents.length === 0 && !agentRunning && (
						<div className="flex items-center justify-center py-8 text-muted-foreground text-xs">
							Send a task to start the AI agent
						</div>
					)}
					{agentEvents.map((event, i) => renderEvent(event, i))}
					<div ref={messagesEndRef} />
				</div>
			</ScrollArea>

			{/* Input bar */}
			<div className="p-2 border-t bg-background">
				<div className="flex gap-2 items-end">
					<div className="flex-1 max-h-40 overflow-y-auto rounded-md border bg-background px-3 py-2">
						<textarea
							placeholder="e.g. Scroll TikTok FYP and like 3 videos..."
							value={agentTask}
							onChange={(e) => {
								onAgentTaskChange(e.target.value);
								// Auto-resize
								e.target.style.height = 'auto';
								e.target.style.height = e.target.scrollHeight + 'px';
							}}
							className="w-full resize-none bg-transparent text-xs focus:outline-none placeholder:text-muted-foreground"
							style={{ minHeight: '1.5rem' }}
							rows={1}
							disabled={agentRunning}
							onKeyDown={(e) => {
								if (e.key === 'Enter' && !e.shiftKey && agentTask && !agentRunning) {
									e.preventDefault();
									onAgentStart();
								}
							}}
						/>
					</div>
					<Input
						type="number"
						value={maxSteps}
						onChange={(e) => onMaxStepsChange(Math.max(1, parseInt(e.target.value) || 500))}
						className="w-16 h-8 text-xs text-center shrink-0"
						title="Max steps"
						disabled={agentRunning}
						min={1}
					/>
					{!agentRunning ? (
						<Button size="sm" onClick={onAgentStart} disabled={!agentTask} className="h-8 shrink-0">
							<Send className="h-3 w-3" />
						</Button>
					) : (
						<Button size="sm" variant="destructive" onClick={onAgentStop} className="h-8 shrink-0">
							<Square className="h-3 w-3" />
						</Button>
					)}
				</div>
			</div>
		</div>
	);
};

// === Group Control Mode ===

const GroupControlMode = ({
	devices,
	syncedIds,
	masterPadId,
	onToggleSync,
	onPromoteToMaster,
	onBack,
}: {
	devices: VMOSDevice[];
	syncedIds: Set<string>;
	masterPadId: string;
	onToggleSync: (padId: string) => void;
	onPromoteToMaster: (padId: string) => void;
	onBack: () => void;
}) => {
	const [mode, setMode] = useState<'manual' | 'agent'>('manual');
	const [agentTask, setAgentTask] = useState('');
	const [agentRunning, setAgentRunning] = useState(false);
	const [agentId, setAgentId] = useState<string | null>(null);
	const [agentEvents, setAgentEvents] = useState<any[]>([]);
	const [maxSteps, setMaxSteps] = useState(200);
	const abortControllerRef = useRef<AbortController | null>(null);
	const currentAgentIdRef = useRef<string | null>(null);
	const queryClient = useQueryClient();

	const masterDevice = devices.find(d => d.pad_id === masterPadId);

	const handleMasterClick = useCallback(async (e: React.MouseEvent<HTMLDivElement>) => {
		const rect = e.currentTarget.getBoundingClientRect();
		const x = (e.clientX - rect.left) / rect.width;
		const y = (e.clientY - rect.top) / rect.height;

		try {
			await sendTouch({ x, y, action_type: 'tap' });
		} catch (err) {
			toast.error('Touch failed');
		}
	}, []);

	const handleKey = useCallback(async (keycode: number) => {
		try {
			await sendKey(keycode);
		} catch (err) {
			toast.error('Key failed');
		}
	}, []);

	const handleTikTokAction = useCallback(async (action: string, text?: string) => {
		try {
			await executeTikTokAction({ action, text });
			toast.success(`${action} sent`);
		} catch (err) {
			toast.error(`${action} failed`);
		}
	}, []);

	const handleAgentStart = useCallback(async () => {
		// Abort previous stream if still running (#11)
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
			abortControllerRef.current = null;
		}

		try {
			const result = await startAgent({ task: toAscii(agentTask.trim()), max_steps: maxSteps });
			const thisAgentId = result.agent_id;
			setAgentId(thisAgentId);
			currentAgentIdRef.current = thisAgentId;
			setAgentRunning(true);
			setAgentEvents([]);

			// Connect to SSE stream with AbortController (#1)
			const controller = new AbortController();
			abortControllerRef.current = controller;

			const token = await getAuthToken();
			const url = getAgentStreamUrl(thisAgentId);
			const response = await fetch(url, {
				headers: { Authorization: `Bearer ${token}` },
				signal: controller.signal,
			});

			if (!response.ok || !response.body) {
				throw new Error('Failed to connect to agent stream');
			}

			const reader = response.body.getReader();
			const decoder = new TextDecoder();

			const readStream = async () => {
				let buffer = '';
				try {
					while (true) {
						const { done, value } = await reader.read();
						if (done) break;

						buffer += decoder.decode(value, { stream: true });
						const lines = buffer.split('\n');
						buffer = lines.pop() || '';

						for (const line of lines) {
							if (line.startsWith('data: ')) {
								try {
									const event = JSON.parse(line.slice(6));
									// Only update state if this is still the current agent (#11)
									if (currentAgentIdRef.current !== thisAgentId) return;
									setAgentEvents(prev => [...prev, event]);

									if (event.event === 'result' || event.event === 'error') {
										setAgentRunning(false);
									}
								} catch {
									// skip malformed JSON
								}
							}
						}
					}
				} catch (err: any) {
					if (err.name === 'AbortError') return; // expected on abort
					throw err;
				}
				// Only update if still current agent (#11)
				if (currentAgentIdRef.current === thisAgentId) {
					setAgentRunning(false);
				}
			};

			readStream().catch(err => {
				if (err.name === 'AbortError') return;
				toast.error(err.message || 'Agent stream failed');
				if (currentAgentIdRef.current === thisAgentId) {
					setAgentRunning(false);
				}
			});
		} catch (err: any) {
			if (err.name === 'AbortError') return;
			toast.error(err.message || 'Failed to start agent');
			setAgentRunning(false);
		}
	}, [agentTask, maxSteps]);

	const handleAgentStop = useCallback(async () => {
		// Abort the SSE stream (#1)
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
			abortControllerRef.current = null;
		}
		if (agentId) {
			try {
				await stopAgent(agentId);
				setAgentRunning(false);
				toast.success('Agent stopped');
			} catch (err) {
				toast.error('Failed to stop agent');
			}
		}
	}, [agentId]);

	// Cleanup SSE stream on unmount (#1)
	useEffect(() => {
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
				abortControllerRef.current = null;
			}
		};
	}, []);

	return (
		<div className="flex flex-col h-full">
			{/* Header with mode toggle */}
			<div className="flex items-center gap-2 p-3 border-b">
				<Button variant="ghost" size="sm" onClick={onBack}>
					<ArrowLeft className="h-4 w-4 mr-1" /> Back
				</Button>
				<h2 className="text-sm font-semibold">
					Group Control — {devices.length} devices, {syncedIds.size} synced
				</h2>
				<div className="flex bg-muted rounded-lg p-0.5 ml-auto">
					<Button
						variant={mode === 'manual' ? 'default' : 'ghost'}
						size="sm"
						onClick={() => setMode('manual')}
					>
						<Hand className="h-3 w-3 mr-1" /> Manual
					</Button>
					<Button
						variant={mode === 'agent' ? 'default' : 'ghost'}
						size="sm"
						onClick={() => setMode('agent')}
					>
						<Bot className="h-3 w-3 mr-1" /> AI Agent
					</Button>
				</div>
				<Button
					variant="ghost"
					size="sm"
					onClick={() => {
						queryClient.invalidateQueries({ queryKey: ['vmos-devices'] });
					}}
				>
					<RefreshCw className="h-4 w-4" />
				</Button>
			</div>

			{/* Main content */}
			<div className="flex flex-1 overflow-hidden">
				{/* Master device (large) */}
				<div className="flex-1 p-3">
					{masterDevice && (
						<div className="h-full flex flex-col">
							<div className="text-xs text-muted-foreground mb-1 flex items-center gap-2">
								<Badge variant="default" className="text-[10px]">MASTER</Badge>
								{masterDevice.device_name}
								{masterDevice.username && <span className="text-primary">@{masterDevice.username}</span>}
							</div>
							<LiveScreenshot
								padId={masterPadId}
								interval={500}
								quality={70}
								className="flex-1 cursor-crosshair min-h-0"
								onClick={handleMasterClick}
							/>
						</div>
					)}
				</div>

				{/* Control buttons */}
				<div className="p-3 flex items-center">
					<ControlButtonBar onKey={handleKey} />
				</div>

				{/* Right sidebar — wider in agent mode with stacked layout */}
				<div className={cn(
					"border-l flex flex-col overflow-hidden",
					mode === 'agent' ? 'w-96' : 'w-72'
				)}>
					{/* Followers section */}
					<div className={cn(
						"p-3 overflow-y-auto",
						mode === 'agent' ? 'max-h-[40%]' : 'flex-1'
					)}>
						<div className="text-xs text-muted-foreground mb-2 font-semibold">FOLLOWERS</div>
						<FollowerDeviceGrid
							devices={devices}
							syncedIds={syncedIds}
							masterPadId={masterPadId}
							onToggleSync={onToggleSync}
							onPromoteToMaster={onPromoteToMaster}
						/>
					</div>

					{/* Agent chat panel — only in agent mode */}
					{mode === 'agent' && (
						<AgentChatPanel
							agentTask={agentTask}
							onAgentTaskChange={setAgentTask}
							onAgentStart={handleAgentStart}
							onAgentStop={handleAgentStop}
							agentRunning={agentRunning}
							agentEvents={agentEvents}
							maxSteps={maxSteps}
							onMaxStepsChange={setMaxSteps}
						/>
					)}
				</div>
			</div>

			{/* Bottom bar — manual mode only */}
			{mode === 'manual' && (
				<BottomControlBar
					onTikTokAction={handleTikTokAction}
					syncedPadIds={Array.from(syncedIds)}
				/>
			)}
		</div>
	);
};

// === Main Page ===

export default function VMOSGroupControlPage() {
	const [pageMode, setPageMode] = useState<'setup' | 'control'>('setup');
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [syncedIds, setSyncedIds] = useState<Set<string>>(new Set());
	const [masterPadId, setMasterPadId] = useState<string | null>(null);

	const { data: devicesData, isLoading, isError, error } = useQuery<DevicesResponse>({
		queryKey: ['vmos-devices'],
		queryFn: fetchDevices,
		staleTime: 30_000,
	});

	const devices = devicesData?.devices || [];

	// Initialize sync state from server
	useEffect(() => {
		if (devicesData) {
			if (devicesData.sync_group.length > 0) {
				setSyncedIds(new Set(devicesData.sync_group));
			}
			if (devicesData.master_pad_id) {
				setMasterPadId(devicesData.master_pad_id);
			}
		}
	}, [devicesData]);

	const handleToggleSelected = useCallback((padId: string) => {
		setSelectedIds(prev => {
			const next = new Set(prev);
			if (next.has(padId)) {
				next.delete(padId);
			} else {
				next.add(padId);
			}
			return next;
		});
	}, []);

	const handleStartGroupControl = useCallback(async () => {
		const ids = Array.from(selectedIds);
		if (ids.length === 0) return;

		try {
			// Set first selected as master
			const master = ids[0];
			await setMasterDevice(master);
			setMasterPadId(master);

			// Set all selected as sync group
			await setSyncGroup(ids);
			setSyncedIds(new Set(ids));

			setPageMode('control');
			toast.success('Group control started');
		} catch (err) {
			toast.error('Failed to start group control');
		}
	}, [selectedIds]);

	const handleToggleSync = useCallback(async (padId: string) => {
		let nextIds: string[];
		setSyncedIds(prev => {
			const next = new Set(prev);
			if (next.has(padId)) {
				next.delete(padId);
			} else {
				next.add(padId);
			}
			nextIds = Array.from(next);
			return next;
		});
		// API call outside state updater (#12)
		try {
			await setSyncGroup(nextIds!);
		} catch (err) {
			toast.error('Failed to update sync group');
		}
	}, []);

	const handlePromoteToMaster = useCallback(async (padId: string) => {
		try {
			await setMasterDevice(padId);
			setMasterPadId(padId);
			toast.success(`${padId} is now master`);
		} catch (err) {
			toast.error('Failed to set master');
		}
	}, []);

	return (
		<AppShell>
			{/* Page header */}
			<PageHeader>
					<SidebarTrigger>
						<PanelLeftOpen className="h-4 w-4" />
					</SidebarTrigger>
					<div className="flex items-center gap-2">
						<Smartphone className="h-5 w-5 text-primary" />
						<h1 className="text-lg font-semibold">Devices</h1>
					</div>
				</PageHeader>

				{/* Content */}
				<div className={cn("flex-1 min-h-0", pageMode === 'control' ? "overflow-hidden" : "overflow-y-auto")}>
					{isLoading ? (
						<div className="flex items-center justify-center h-full">
							<Loader2 className="h-8 w-8 animate-spin text-primary" />
						</div>
					) : isError ? (
						<div className="flex items-center justify-center h-full text-destructive">
							Error: {(error as Error)?.message || 'Failed to load devices'}
						</div>
					) : pageMode === 'setup' ? (
						<div className="p-6">
							<SetupMode
								devices={devices}
								selectedIds={selectedIds}
								onToggle={handleToggleSelected}
								onStart={handleStartGroupControl}
								isLoading={false}
							/>
						</div>
					) : masterPadId ? (
						<GroupControlMode
							devices={devices.filter(d => selectedIds.has(d.pad_id))}
							syncedIds={syncedIds}
							masterPadId={masterPadId}
							onToggleSync={handleToggleSync}
							onPromoteToMaster={handlePromoteToMaster}
							onBack={() => setPageMode('setup')}
						/>
					) : null}
				</div>
		</AppShell>
	);
}
